import { css, cx } from "@emotion/css";
import { space } from "@octopusdeploy/design-system-tokens";
import * as React from "react";
import type { IconOnlyButtonProps } from "../../Button";
import type { PopoverBasicHelpProps } from "../../Popover";
import { FormDescription } from "../Primitives/FormDescription";
import { Input } from "../Primitives/Input";
import { InputError } from "../Primitives/InputError";
import { InputLabel } from "../Primitives/InputLabel";
import type { DescriptionContent } from "../utils/descriptionWithLinks";
type BaseTextFieldProps<T> = {
    value?: T;
    /**
     * The label for the input field
     */
    label: string;
    /**
     * The placeholder text to display when input is empty
     */
    placeholder?: string;
    /**
     * The description text to display below the input
     */
    description?: string | DescriptionContent;
    /**
     * Error message to display
     */
    error?: string;
    /**
     * Whether the field is disabled
     */
    disabled?: boolean;
    /**
     * Whether the field is required
     */
    hasRequiredMarker?: boolean;
    /**
     * Whether to show optional marker
     */
    hasOptionalMarker?: boolean;
    /**
     * Whether this field has a default value marker
     */
    hasDefaultMarker?: boolean;
    /**
     * PopoverBasicHelp component to display additional help information
     */
    popover?: React.ReactElement<PopoverBasicHelpProps>;
    /**
     * Whether to autofocus the input
     */
    autoFocus?: boolean;
    /**
     * Whether the input is readonly
     */
    readOnly?: boolean;
    /**
     * The name attribute for the input
     */
    name?: string;
    /**
     * The prefix to display before the input - can be a string or a icon it cannot be interactive
     */
    prefix?: string | React.ReactElement;
    /**
     * The suffix to display after the input - can be a string or a button with ghost importance and medium size only
     */
    suffix?: string | React.ReactElement<IconOnlyButtonProps>;
    /**
     * The action to perform when the form control field is changed.
     */
    onChange: (newValue: T) => void;
};
type TextFieldPropsWithNumberType<T> = BaseTextFieldProps<T> & {
    /**
     * The input type must be "number" to use min, max, and step
     */
    type: "number";
    /**
     * The minimum value allowed
     */
    min?: number;
    /**
     * The maximum value allowed
     */
    max?: number;
    /**
     * The step value for the number input
     */
    step?: number | "any";
};
type TextFieldPropsRemainingTypes<T> = BaseTextFieldProps<T> & {
    /**
     * The input type (text, email, password, etc.)
     */
    type?: "text" | "email" | "password" | "tel" | "url" | "search" | "time";
};
export type TextFieldProps<T> = TextFieldPropsWithNumberType<T> | TextFieldPropsRemainingTypes<T>;
/**
 * TextField component
 * @param props - TextFieldProps
 * @param props.label - The label for the input field
 * @param props.value - The current value of the input
 * @param props.type - The input type (text, email, password, etc.)
 * @param props.placeholder - The placeholder text to display when input is empty
 * @param props.description - The description text to display below the input
 * @param props.error - Error message to display
 * @param props.disabled - Whether the field is disabled
 * @param props.hasRequiredMarker - Whether the field is required
 * @param props.hasOptionalMarker - Whether to show optional marker
 * @param props.hasDefaultMarker - Whether this field has a default value marker
 * @param props.popover - PopoverBasicHelp component to display additional help information e.g. <PopoverBasicHelp placement="right-start" description="A popover" />
 * @param props.autoFocus - Whether to autofocus the input
 * @param props.readOnly - Whether the input is readonly
 * @param props.name - The name attribute for the input
 * @param props.prefix - The prefix to display before the input - can be a string or a icon it cannot be interactive
 * @param props.suffix - The suffix to display after the input - can be a string or a button with ghost importance and medium size only
 * @param props.onChange - The action to perform when the form control field is changed
 *
 * @returns TextField component
 */
export function TextField<T extends string | number | undefined>(props: TextFieldProps<T>) {
    const { label, value, placeholder, description, error, onChange, disabled = false, hasRequiredMarker = false, hasOptionalMarker = false, hasDefaultMarker = false, popover, type = "text", autoFocus = false, readOnly = false, name, prefix, suffix, } = props;
    // min, max, step only when type is "number"
    const min = props.type === "number" ? props.min : undefined;
    const max = props.type === "number" ? props.max : undefined;
    const step = props.type === "number" ? props.step : undefined;
    const baseId = React.useId();
    const inputId = `textfield-${name ? name + "-" : ""}${baseId}`;
    return (<div className={cx(containerStyles)}>
            <div>
                <InputLabel label={label} htmlFor={inputId} isDisabled={disabled} hasRequiredMarker={hasRequiredMarker} hasOptionalMarker={hasOptionalMarker} hasDefaultMarker={hasDefaultMarker} popover={popover}/>
                {description && <FormDescription id={`${inputId}-description`} description={description} isDisabled={disabled}/>}
            </div>
            <Input id={inputId} type={type} value={value} placeholder={placeholder} disabled={disabled} required={hasRequiredMarker} readOnly={readOnly} autoFocus={autoFocus} min={min} max={max} step={step} name={name} prefix={prefix} suffix={suffix} onChange={onChange} aria-invalid={!!error} aria-describedby={[description ? `${inputId}-description` : null, error ? `${inputId}-error` : null].filter(Boolean).join(" ") || undefined}/>

            {error && <InputError error={error} id={`${inputId}-error`}/>}
        </div>);
}
const containerStyles = css({
    display: "grid",
    position: "relative",
    gap: space[8],
    width: "100%",
    maxWidth: "600px",
});
